// features/dashboard/dashboardAPI.js
import axios from 'axios';
import moment from 'moment';

const API_BASE = process.env.REACT_APP_API_URL || 'https://alcobrakeadmin.com/v3/us';

// Helper function to parse JSON strings
const parseJSONField = (data) => {
  if (!data) return null;
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (e) {
    return data;
  }
};

// Fetch Dashboard Data
export const fetchDashboardDataAPI = async (userData) => {
  let endpoint = '';
  
  if (userData.role === 'Admin' || userData.role === 'superadmin') {
    endpoint = `${API_BASE}/cadbdata/getdata`;
  } else if (userData.role === 'dealer' || userData.role === 'school') {
    endpoint = `${API_BASE}/cadbdata/getdealerdata?dealerName=${userData.dealerId}`;
  } else {
    endpoint = `${API_BASE}/cadbdata/getofficedata?officeId=${userData.officeId}`;
  }

  const response = await axios.get(endpoint);
  const data = response.data;
  const today = moment();
  let currentRevenue = 0;
  let totalRevenue = 0;
  const calibrations = [];
  
  // Process calibrations
  if (data.calibrations && Array.isArray(data.calibrations)) {
    const sortedCalibrations = [...data.calibrations]
      .sort((a, b) => new Date(a.time) - new Date(b.time))
      .slice(-5);
    
    sortedCalibrations.forEach(cal => {
      calibrations.push(parseJSONField(cal));
    });
  }
  
  // Process sales for revenue and charts
  const last6Months = [];
  const monthsList = moment.monthsShort();
  const currentDate = moment();
  
  for (let i = 5; i >= 0; i--) {
    const monthDate = moment(currentDate).subtract(i, 'M');
    last6Months.push({
      month: monthsList[monthDate.month()],
      year: monthDate.year(),
      revenue: 0,
    });
  }
  
  if (data.sales && Array.isArray(data.sales)) {
    data.sales.forEach(sale => {
      const parsedSale = parseJSONField(sale);
      if (parsedSale && parsedSale.amountpaid) {
        const amount = parseFloat(parsedSale.amountpaid);
        totalRevenue += amount;
        
        const saleMonth = moment(parsedSale.date).month();
        const currentMonth = today.month();
        if (saleMonth === currentMonth) {
          currentRevenue += amount;
        }
        
        // Add to monthly data
        const saleDate = moment(parsedSale.date);
        last6Months.forEach(monthData => {
          if (monthData.month === monthsList[saleDate.month()] && 
              monthData.year === saleDate.year()) {
            monthData.revenue += amount;
          }
        });
      }
    });
  }
  
  const salesData = last6Months.map(month => ({
    name: `${month.month}`,
    revenue: month.revenue,
    profit: month.revenue * 0.7,
    expenses: month.revenue * 0.3,
  }));
  
  return {
    metrics: {
      totalDevices: data.totaldevices || 0,
      availableDevices: data.availabledevices || 0,
      currentRevenue: currentRevenue,
      totalRevenue: totalRevenue,
      newClients: data.newclients || 0,
      totalClients: data.totalclients || 0,
      newInstallations: data.newinstallations || 0,
      removalsThisMonth: data.removalthismonth || 0,
    },
    charts: {
      salesData: salesData,
      salesLabels: salesData.map(d => d.name),
      salesValues: salesData.map(d => d.revenue),
    },
    calibrations: calibrations,
  };
};

// Fetch Lockout Devices
export const fetchLockoutDevicesAPI = async (userData) => {
  let endpoint = '';
  
  if (userData.role === 'Admin' || userData.role === 'superadmin') {
    endpoint = `${API_BASE}/cadevices/getalllockoutdevices`;
  } else {
    endpoint = `${API_BASE}/cadevices/getlockoutdevices?dealerName=${userData.dealerId}`;
  }
  
  const response = await axios.get(endpoint);
  
  if (response.data && Array.isArray(response.data)) {
    return response.data.map(device => ({
      ...device,
      typeofevent: device.typeofevent >= 220 && device.typeofevent <= 227
        ? `LOCKOUT ${device.typeofevent - 220}`
        : 'NA',
    }));
  }
  
  return [];
};

// Check Payment Status
export const checkPaymentStatusAPI = async (customerId) => {
  if (!customerId || customerId.length <= 5) {
    return {
      hasAccess: false,
      isPaymentDue: false,
      header: 'Dealer Not Authorized',
      message: 'Devices Not Assigned to the Dealer',
    };
  }
  
  try {
    const response = await axios.get(
      `${API_BASE}/cadealerpayments/checkdealerpaymentnew?customer_id=${customerId}`
    );
    
    if (!response.data.data) {
      return {
        hasAccess: false,
        isPaymentDue: false,
        header: 'Dealer Not Authorized',
        message: 'Devices Not Assigned to the Dealer',
      };
    }
    
    if (response.data.data.length > 0 && response.data.paymentdue) {
      return {
        hasAccess: false,
        isPaymentDue: true,
        header: 'Payment Pending',
        message: 'Your payment is currently pending',
      };
    }
    
    return {
      hasAccess: true,
      isPaymentDue: false,
      header: '',
      message: '',
    };
  } catch (error) {
    return {
      hasAccess: false,
      isPaymentDue: false,
      header: 'Error',
      message: 'Something went wrong. Please try again.',
    };
  }
};

// Fetch Invoices
export const fetchInvoicesAPI = async (dealerId) => {
  const response = await axios.get(
    `${API_BASE}/cadealerpayments/getdealerinvoices?dealer=${dealerId}`
  );
  
  if (response.data && Array.isArray(response.data)) {
    return response.data.filter(invoice => invoice.paymentstatus === 'unpaid');
  }
  
  return [];
};

// Create Payment Intent
export const createPaymentIntentAPI = async (dealerId, amount) => {
  const formattedAmount = amount.toFixed(2).replace(/\./g, '');
  const response = await axios.get(
    `${API_BASE}/payments/getsecretkeynew?dealerid=25&amount=${formattedAmount}`
  );
  
  if (response.data.client_secret) {
    return {
      clientSecret: response.data.client_secret,
      intentId: response.data.id,
      amount: amount,
    };
  }
  
  throw new Error('Failed to create payment intent');
};

// Cancel Payment
export const cancelPaymentAPI = async (paymentIntentId) => {
  await axios.get(
    `${API_BASE}/payments/cancelintentnew?dealerid=25&intent=${paymentIntentId}`
  );
  return { success: true };
};

// Process Cash Payment
export const processCashPaymentAPI = async (dealerId, adminPassword, paymentIntentId, userEmail) => {
  // Verify admin password
  const authResponse = await axios.get(
    `${API_BASE}/calogin/getuniquelogin?emailId=admin@alcobrake.com&password=${adminPassword}`
  );
  
  if (!authResponse.data || authResponse.data[0]?.password !== adminPassword) {
    throw new Error('Incorrect Password');
  }
  
  // Cancel the payment intent
  await axios.get(
    `${API_BASE}/payments/cancelintentnew?dealerid=25&intent=${paymentIntentId}`
  );
  
  // Clear dealer payments
  const clearResponse = await axios.post(
    `${API_BASE}/cadealerpayments/clearpayments/${dealerId}`
  );
  
  if (clearResponse.data === 'Dealer Payment cleared') {
    // Send email notification
    const formData = new FormData();
    formData.append('to', userEmail);
    formData.append('message', `Dear dealer, your outstanding payment has been successfully processed and cleared in cash mode - Alcobrake.`);
    formData.append('subject', 'SUCCESS TRANSACTION');
    
    await axios.post(`${API_BASE}/v2/email/sendemail`, formData);
    return { success: true };
  }
  
  throw new Error('Failed to clear payments');
};